import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banknote, CheckCircle2, CreditCard, ExternalLink, Loader2, AlertCircle, X } from 'lucide-react';
import { isPayoutDestinationReady } from '../admin/usePayoutSetupStatus';
import { apiClient } from '../../../lib/apiClient';

// CTV-518: CTVBuyer is merchant of record, so advertiser charges land on the
// platform account and each publisher's share goes out as a Stripe transfer.
// CTV-719 / ADR-0035: one transfer per campaign per calendar month, sent
// exactly 60 days after that DELIVERY month's last day. The old "60-90 days
// after the charge" wording predates the settlement engine and was wrong on
// both counts: the clock starts when the ads ran, not when the advertiser
// paid, and the lag is exact rather than a range.
//
// This section connects a payout destination (recipient account via hosted
// onboarding, or the publisher's own Stripe account via Connect OAuth) and
// shows readiness. It used to be the whole "Payments (Stripe)" page; CTV-776
// folded it into the Billing tab and CTV-787 moved it to the Payout method
// tab of Station settings, where the ledger it is not (rows to read versus
// configuration to write) stops sharing a page with it. Behavior is unchanged
// across all three: the same /stripe/* calls, the same three cards, the same
// return-trip handling.

interface PublisherStripeRow {
  slug: string;
  stripe_account_id: string | null;
  stripe_account_mode: 'recipient' | 'oauth' | null;
  stripe_payouts_enabled: boolean;
  stripe_details_submitted: boolean;
  stripe_onboarding_started_at: string | null;
  stripe_onboarding_completed_at: string | null;
}

// Shape returned by POST /stations/payout-status (CTV-759): one row per
// station in scope. GET /stations/:slug is @Public() and stopped carrying the
// Stripe block in CTV-759, so reading it there threw "Cannot read properties
// of undefined (reading 'accountId')" (the pre-CTV-776 Payments page had that
// bug on dev; CTV-776 fixes it here by reading the scoped route).
interface PayoutStatusApi {
  rows: Array<{
    slug: string | null;
    accountId: string | null;
    accountMode: 'recipient' | 'oauth' | null;
    payoutsEnabled: boolean | null;
    detailsSubmitted: boolean | null;
    onboardingStartedAt: string | null;
    onboardingCompletedAt: string | null;
  }>;
}

// CTV-787: the ledger is no longer above this section, it is the Billing
// tab, so the note links there rather than pointing at a neighbour.
const PayoutTimingNote: React.FC<{ slug: string }> = ({ slug }) => (
  <>
    Each month of delivery is paid 60 days after that month ends, based on when your ads ran.
    Each month&apos;s payout appears on the{' '}
    <Link to={`/admin/${slug}/billing`} className="underline underline-offset-2 hover:text-slate-700">
      Billing tab
    </Link>{' '}
    as it becomes payable.
  </>
);

interface PayoutMethodSectionProps {
  slug: string;
  /** Called after any Stripe status refresh so the page's status strip can re-read readiness. */
  onStatusChange?: () => void;
}

const PayoutMethodSection: React.FC<PayoutMethodSectionProps> = ({ slug, onStatusChange }) => {
  const [params, setParams] = useSearchParams();
  const [row, setRow] = React.useState<PublisherStripeRow | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [actionBusy, setActionBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Dismissible notice for the ?stripe_oauth=connected|denied return trip.
  const [oauthNotice, setOauthNotice] = React.useState<'connected' | 'denied' | null>(null);

  const refresh = React.useCallback(async () => {
    try {
      const { rows } = await apiClient.post<PayoutStatusApi>('/stations/payout-status', {
        scope: 'station',
        slug,
      });
      const r = rows?.[0];
      if (!r) throw new Error('Failed to load station');
      setRow({
        slug: r.slug ?? slug,
        stripe_account_id: r.accountId,
        stripe_account_mode: r.accountMode,
        stripe_payouts_enabled: r.payoutsEnabled === true,
        stripe_details_submitted: r.detailsSubmitted === true,
        stripe_onboarding_started_at: r.onboardingStartedAt,
        stripe_onboarding_completed_at: r.onboardingCompletedAt,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load station');
    }
    setLoading(false);
  }, [slug]);

  // After return from Stripe, ask the API to re-fetch and persist account state.
  const refreshFromStripe = React.useCallback(async () => {
    try {
      await apiClient.post('/stripe/account-status', { slug });
      await refresh();
      onStatusChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to refresh status');
    }
  }, [slug, refresh, onStatusChange]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // If we just came back from Stripe-hosted onboarding, refresh status.
  React.useEffect(() => {
    if (params.get('return') === '1' && row?.stripe_account_id) {
      refreshFromStripe();
    }
  }, [params, row?.stripe_account_id, refreshFromStripe]);

  // OAuth return trip: surface the outcome and (on success) refresh status.
  React.useEffect(() => {
    const outcome = params.get('stripe_oauth');
    if (outcome !== 'connected' && outcome !== 'denied') return;
    setOauthNotice(outcome);
    if (outcome === 'connected') refreshFromStripe();
    // Strip the param so a reload doesn't re-trigger the notice.
    const next = new URLSearchParams(params);
    next.delete('stripe_oauth');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const startBankOnboarding = async () => {
    setActionBusy(true);
    setError(null);
    try {
      const baseReturn = `${window.location.origin}/admin/${slug}/settings/payout-method`;
      const { url } = await apiClient.post<{ url: string }>('/stripe/connect-link', {
        slug,
        returnUrl: `${baseReturn}?return=1`,
        refreshUrl: `${baseReturn}?refresh=1`,
      });
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start onboarding');
      setActionBusy(false);
    }
  };

  const startOauthConnect = async () => {
    setActionBusy(true);
    setError(null);
    try {
      const { url } = await apiClient.post<{ url: string }>('/stripe/oauth-link', {
        slug,
        returnUrl: `${window.location.origin}/admin/${slug}/settings/payout-method`,
      });
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start Stripe connection');
      setActionBusy(false);
    }
  };

  const isOauth = row?.stripe_account_mode === 'oauth';
  // CTV-743: the settlement cron's gate, shared with the sidebar indicator and
  // the overview banner. This used to be "account id AND (oauth OR details
  // submitted)", which could show a green "connected" card to a publisher the
  // engine would refuse to pay: details_submitted only means the Stripe form
  // was completed, not that transfers can land. Anyone who is genuinely
  // mid-verification still lands on the in-progress card below (branch 2),
  // never back on the two connect buttons.
  const isConnected = isPayoutDestinationReady(
    row && {
      accountId: row.stripe_account_id,
      accountMode: row.stripe_account_mode,
      payoutsEnabled: row.stripe_payouts_enabled,
    },
  );

  return (
    <section aria-labelledby="payout-method-heading" data-testid="payout-method-section">
      <div className="mb-5">
        <h2 id="payout-method-heading" className="text-lg font-semibold text-slate-900">
          Payout method
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Where CTVBuyer sends this station&apos;s share of each month&apos;s delivery.
        </p>
      </div>

      {oauthNotice && (
        <div
          className={`mb-6 flex items-start justify-between gap-3 rounded-lg border p-4 text-sm ${
            oauthNotice === 'connected'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-amber-50 border-amber-200 text-amber-900'
          }`}
        >
          <span>
            {oauthNotice === 'connected'
              ? 'Stripe account connected. Payouts will be sent to this account.'
              : 'Stripe connection was canceled: no account was connected.'}
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setOauthNotice(null)}
            className="shrink-0 text-current hover:opacity-70"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
      ) : isConnected && row ? (
        <ConnectedCard row={row} onRefresh={refreshFromStripe} />
      ) : row?.stripe_account_id ? (
        <InProgressCard row={row} isOauth={isOauth} onResume={startBankOnboarding} onRefresh={refreshFromStripe} busy={actionBusy} />
      ) : (
        <NotConnectedCard slug={slug} onConnectBank={startBankOnboarding} onConnectStripe={startOauthConnect} busy={actionBusy} />
      )}

      {error && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>}
    </section>
  );
};

function NotConnectedCard({
  slug,
  onConnectBank,
  onConnectStripe,
  busy,
}: {
  slug: string;
  onConnectBank: () => void;
  onConnectStripe: () => void;
  busy: boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3"><Banknote className="w-5 h-5 text-slate-700" /><h3 className="text-base font-semibold text-slate-900">No payout method connected</h3></div>
      <p className="text-sm text-slate-600 mb-5">
        Connect a bank account through Stripe&apos;s secure setup page: no Stripe account or login
        needed. Already use Stripe for your business? Connect your existing account instead.
      </p>
      <div className="flex flex-wrap gap-2">
        <button onClick={onConnectBank} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#635bff] hover:bg-[#4d44e0] disabled:opacity-60 text-white text-sm font-medium rounded-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
          Connect bank account
        </button>
        <button onClick={onConnectStripe} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-sm font-medium rounded-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          Connect existing Stripe account
        </button>
      </div>
      <p className="mt-4 text-xs text-slate-500"><PayoutTimingNote slug={slug} /></p>
    </div>
  );
}

function InProgressCard({ row, isOauth, onResume, onRefresh, busy }: { row: PublisherStripeRow; isOauth: boolean; onResume: () => void; onRefresh: () => void; busy: boolean }) {
  return (
    <div className="bg-white border border-amber-200 bg-amber-50 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3"><AlertCircle className="w-5 h-5 text-amber-700" /><h3 className="text-base font-semibold text-slate-900">Payout setup in progress</h3></div>
      <p className="text-sm text-slate-700 mb-2">Stripe account: <span className="font-mono text-xs">{row.stripe_account_id}</span></p>
      <p className="text-sm text-slate-700 mb-2">Details submitted: {row.stripe_details_submitted ? 'yes' : 'no'} · Payouts enabled: {row.stripe_payouts_enabled ? 'yes' : 'no'}</p>
      {/* CTV-743: an OAuth station reaches this card only when its own Stripe
          account cannot receive transfers, and we cannot fix that for them.
          "Resume setup" would call connect-link, which rejects oauth mode
          outright ("disconnect the existing Stripe account first"), so offering
          it here would hand them a button that only errors. */}
      {isOauth ? (
        <>
          <p className="text-sm text-slate-700 mb-5">
            Payouts are not enabled on your Stripe account yet. Finish any outstanding
            requirements in your own Stripe dashboard, then refresh.
          </p>
          <button onClick={onRefresh} className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-sm font-medium rounded-md">Refresh status</button>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-700 mb-5">
            We cannot send payouts until Stripe finishes verifying this account.
          </p>
          <div className="flex gap-2">
            <button onClick={onResume} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-[#635bff] hover:bg-[#4d44e0] disabled:opacity-60 text-white text-sm font-medium rounded-md">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Resume setup
            </button>
            <button onClick={onRefresh} className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-sm font-medium rounded-md">Refresh status</button>
          </div>
        </>
      )}
    </div>
  );
}

function ConnectedCard({ row, onRefresh }: { row: PublisherStripeRow; onRefresh: () => void }) {
  const isOauth = row.stripe_account_mode === 'oauth';
  const methodLabel = isOauth ? 'Stripe account' : 'Bank account (via Stripe)';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        <h3 className="text-base font-semibold text-slate-900">Payout method connected</h3>
      </div>
      <div className="space-y-2 text-sm text-slate-700 mb-5">
        {/* CTV-787: the value alone. The section heading, its lead and this
            card's own heading all say "payout method" already, so the label
            here was the fourth time in one card. */}
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-slate-500" />
          <span className="font-medium">{methodLabel}</span>
        </div>
        <div>
          Payouts enabled:{' '}
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded ${
              row.stripe_payouts_enabled
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            {row.stripe_payouts_enabled ? 'yes' : 'no'}
          </span>
        </div>
      </div>
      <button onClick={onRefresh} className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-sm font-medium rounded-md">
        Refresh status
      </button>
      <p className="mt-4 text-xs text-slate-500"><PayoutTimingNote slug={row.slug} /></p>
      {isOauth && (
        <p className="mt-1 text-xs text-slate-500">Manage payouts in your Stripe dashboard.</p>
      )}
    </div>
  );
}

export default PayoutMethodSection;
